import Hls from 'hls.js';

/**
 * Attaches an HLS stream to one `<audio>` element.
 *
 * ## Two completely different code paths, and both are needed
 *
 * hls.js works by feeding segments into Media Source Extensions. **iOS Safari
 * has no MSE at all**, so hls.js does not work there in any version - but
 * Safari and iOS play HLS natively from a plain `src`. So the rule is: use MSE
 * where there is MSE, and hand the manifest over natively only where there is
 * not (HANDOFF §9.1).
 *
 * ## MSE first, and the order is load-bearing
 *
 * This used to ask `canPlayType('application/vnd.apple.mpegurl')` first, on the
 * stated belief that only Safari answers it. That is no longer true: Chromium
 * shipped native HLS and now answers `"maybe"` for `<audio>` and `<video>`
 * alike. So every Chrome user was silently taking the native path.
 *
 * Playback was fine, which is why it went unnoticed for so long. What was not
 * fine is that the native path points the element at a cross-origin URL on the
 * media CDN - and `createMediaElementSource()` on an element whose resource is
 * not CORS-clean produces **silence**, not an error. The browser studio taps
 * this element to mix the deck into a broadcast, so in production it was
 * mixing silence: the host heard nothing through their monitor and listeners
 * got a stream with no music in it. In development the media is same-origin
 * through Vite's proxy, so the tap was clean and every test passed.
 *
 * Feeding through MSE makes the element's source a `blob:` URL, which is
 * same-origin by construction and can never be tainted, whatever the CDN does.
 */
export interface Engine {
  destroy: () => void;
}

/**
 * Whether this browser will play an HLS manifest from a plain `src`.
 *
 * True in Safari, and now also in Chromium - so this answers "can it" and not
 * "should it". The choice of path is made in `attach()`, which prefers MSE.
 */
export function canPlayNatively(audio: HTMLAudioElement): boolean {
  return audio.canPlayType('application/vnd.apple.mpegurl') !== '';
}

export function attach(audio: HTMLAudioElement, url: string, onError: (message: string) => void): Engine {
  /*
   * The native fallback, for browsers with no MSE - which in practice means
   * iOS Safari. Everything else goes through hls.js below. See the note above
   * for why this is the fallback and not the preference.
   */
  if (!Hls.isSupported()) {
    if (!canPlayNatively(audio)) {
      onError('This browser cannot play streamed audio.');

      return { destroy: () => {} };
    }

    audio.src = url;

    return { destroy: () => {
      audio.removeAttribute('src');
      audio.load();
    } };
  }

  const hls = new Hls({
    /*
     * Without this the media cookie is not sent and every segment 403s
     * (HANDOFF §4.4).
     *
     * In production the audio lives on `media.<brand>` while the player is on
     * the app origin, so segment requests are cross-origin and a browser omits
     * credentials from those unless asked. In development they are same-origin
     * through Vite's proxy and this changes nothing - which is precisely why it
     * is easy to omit and only discover after a deploy.
     */
    xhrSetup: (xhr) => {
      xhr.withCredentials = true;
    },

    /*
     * Audio-only, so the defaults tuned for video are wrong.
     *
     * A 30-second forward buffer on a 4-minute track at 192k is under a
     * megabyte - cheap, and it means a brief connection drop is inaudible
     * rather than a stall. hls.js defaults to 30 for video where the same
     * duration is far more data.
     */
    maxBufferLength: 30,
    // Start on the lower rendition and let it climb. The first seconds of a
    // track are when somebody decides whether to keep listening, so time to
    // first note matters more than fidelity for those seconds.
    startLevel: 0,
    enableWorker: true,
  });

  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (!data.fatal) {
      // Non-fatal errors are routine - a segment that needs one retry, a gap
      // in the buffer. hls.js recovers from them on its own and surfacing them
      // would mean showing an error during normal playback.
      return;
    }

    switch (data.type) {
      case Hls.ErrorTypes.NETWORK_ERROR:
        /*
         * A 403 here almost always means an expired media token rather than a
         * genuine network failure - the tokens last six hours and a long
         * listening session can outlive one.
         *
         * `startLoad()` retries, and by then the caller will have re-minted.
         */
        hls.startLoad();
        break;

      case Hls.ErrorTypes.MEDIA_ERROR:
        // A decoding hiccup. hls.js can usually re-sync the buffer without
        // tearing the whole stream down.
        hls.recoverMediaError();
        break;

      default:
        hls.destroy();
        onError('Playback failed.');
    }
  });

  hls.loadSource(url);
  hls.attachMedia(audio);

  return { destroy: () => hls.destroy() };
}
